<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة المسارات</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة المسارات التعليمية</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="openAddModal"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors">
          إضافة مسار جديد
        </button>
      </div>
    </div>

    <!-- Search Section -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في المسارات..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي المسارات: {{ massarats.length }}
      </div>
    </div>

    <!-- Massarats Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">قائمة المسارات</h1>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredMassarats.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
        </svg>
        <p class="mt-2 text-gray-600">
          {{ searchTerm ? 'لا توجد مسارات تطابق البحث' : 'لا توجد مسارات مسجلة' }}
        </p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم المسار</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعديل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حذف</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(massarat, index) in filteredMassarats" :key="massarat.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{{ massarat.value }}</td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openEditModal(massarat)">تعديل</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button 
                      class="text-red-600 hover:text-red-800" 
                      @click="handleDelete(massarat)">
                      حذف
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          عرض {{ pagination.from }} إلى {{ pagination.to }} من {{ pagination.total }} نتائج
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            السابق
          </button>
          <button
            @click="changePage(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            التالي
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 500px;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          {{ editMode ? 'تعديل المسار' : 'إضافة مسار جديد' }}
        </h3>
        
        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="value" class="block text-sm font-medium text-gray-700 mb-2">اسم المسار *</label>
              <input
                v-model="formData.value"
                type="text"
                id="value"
                required
                placeholder="أدخل اسم المسار"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div class="mt-6 flex gap-3 justify-end">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              إلغاء
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-4 py-2 bg-customHafadhColor text-white rounded-md text-sm font-medium hover:bg-customHafadhColor/90 disabled:opacity-50">
              {{ saving ? 'جاري الحفظ...' : (editMode ? 'تحديث' : 'إضافة') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'MassaratsManagement',
  data() {
    return {
      massarats: [],
      loading: false,
      saving: false,
      searchTerm: '',
      showModal: false,
      editMode: false,
      formData: {
        id: null,
        value: '',
      },
      alert: {
        show: false,
        type: 'success',
        message: '',
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        from: 0,
        to: 0,
      },
    }
  },
  computed: {
    filteredMassarats() {
      let filtered = this.massarats

      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase()
        filtered = filtered.filter(massarat => 
          massarat.value?.toLowerCase().includes(search)
        )
      }

      return filtered
    }
  },
  watch: {
    searchTerm() {
      this.pagination.currentPage = 1
    },
  },
  mounted() {
    this.loadMassarats()
  },
  methods: {
    async loadMassarats() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          size: 20,
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }

        const { data } = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.massarats_list,
          {
            params,
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        // Handle nested response structure: data.result.data.data and data.result.data.pagination
        if (data.result && data.result.data) {
          if (Array.isArray(data.result.data)) {
            // Direct array response
            this.massarats = data.result.data
          } else if (data.result.data.data && Array.isArray(data.result.data.data)) {
            // Nested structure: result.data.data
            this.massarats = data.result.data.data
            if (data.result.data.pagination) {
              this.pagination = {
                currentPage: data.result.data.pagination.current_page || 1,
                totalPages: data.result.data.pagination.total_pages || 1,
                total: data.result.data.pagination.total || 0,
                from: data.result.data.pagination.from || 0,
                to: data.result.data.pagination.to || 0,
              }
            }
          } else {
            this.massarats = []
          }
        } else {
          this.massarats = []
        }
      } catch (error) {
        console.error('Failed to load massarats', error)
        this.showAlert('danger', 'فشل في تحميل المسارات')
        this.massarats = []
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editMode = false
      this.formData = {
        id: null,
        value: '',
      }
      this.showModal = true
    },
    openEditModal(massarat) {
      this.editMode = true
      this.formData = {
        id: massarat.id,
        value: massarat.value,
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editMode = false
      this.formData = {
        id: null,
        value: '',
      }
    },
    async handleSave() {
      this.saving = true
      try {
        const payload = {
          value: this.formData.value,
        }

        if (this.editMode) {
          await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.massarats_update + this.formData.id,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم تحديث المسار بنجاح')
        } else {
          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.massarats_create,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم إضافة المسار بنجاح')
        }

        this.closeModal()
        this.loadMassarats()
      } catch (error) {
        console.error('Failed to save massarat', error)
        const message = error.response?.data?.result?.message_ar || error.response?.data?.message_ar || 'فشل في حفظ المسار'
        this.showAlert('danger', message)
      } finally {
        this.saving = false
      }
    },
    async handleDelete(massarat) {
      if (!confirm(`هل أنت متأكد من حذف المسار "${massarat.value}"؟`)) {
        return
      }

      try {
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.massarats_delete + massarat.id,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        )
        this.showAlert('success', 'تم حذف المسار بنجاح')
        this.loadMassarats()
      } catch (error) {
        console.error('Failed to delete massarat', error)
        const message = error.response?.data?.result?.message_ar || error.response?.data?.message_ar || 'فشل في حذف المسار'
        this.showAlert('danger', message)
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page
        this.loadMassarats()
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    },
  },
})
</script>

<style scoped>
.popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.popup-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.enabled-button {
  color: #3b82f6;
  font-weight: 500;
  cursor: pointer;
}

.enabled-button:hover {
  color: #2563eb;
  text-decoration: underline;
}
</style>

